import React, { useEffect, useState, useRef } from 'react';
import { Button as AntButton, Radio, Spin, notification, DatePicker, Checkbox } from 'antd';
import { Modal, Table, SignaturePad } from '_src/components';
import { Select, Input, Filepicker } from '_src/shared/form-helpers';
import { useFetch, useSave } from 'store';
import services from '_src/services';
import moment from 'moment';

import {
  ModalContainer,
  TableText,
  ButtonWrapper,
  UpdateButton,
  FormBox,
  FormGrid,
  FormGridWide,
  Label,
  TextArea,
  ConditionRow,
  CommentsLabel
} from './styles';

const laptopLendingConfig = services['laptopLending'];

const CONDITION_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' }
];

export default function LaptopDetails({
  visible,
  onClose,
  studentId,
  branchId,
  onLoanUpdated = () => {}
}) {
  const [loanHistory, setLoanHistory] = useState([]);
  const [availableLaptops, setAvailableLaptops] = useState([]);
  const [accessoryTypes, setAccessoryTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLendLaptopModal, setShowLendLaptopModal] = useState(false);
  const [showReturnLaptopModal, setShowReturnLaptopModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const selectedLoanRef = useRef(null);
  const [settings, setSettings] = useState({
    requireSignature: false,
    requirePhotoOnIssue: false,
    requirePhotoOnReturn: false,
    requireConditionOnIssue: false,
    requireNotesWhenDamaged: false,
    requireNotesWhenIssuing: false,
    setExpectedReturnDate: false,
    trackAccessories: false
  });

  const [lendLaptopForm, setLendLaptopForm] = useState({
    laptopId: null,
    issueNotes: '',
    studentSignature: '',
    photo: '',
    conditionOnIssue: 'good',
    expectedReturnDate: null,
    accessories: []
  });
  const [signatureRef, setSignatureRef] = useState(null);

  const [returnLaptopForm, setReturnLaptopForm] = useState({
    condition: 'satisfactory',
    returnNotes: '',
    photo: '',
    accessoriesReturned: []
  });

  const { get: fetchLoanHistory } = useFetch({
    ...laptopLendingConfig.getStudentLoanHistory
  });

  const { get: fetchAvailableLaptops } = useFetch({
    ...laptopLendingConfig.getAvailableLaptops
  });

  const { get: fetchSettings } = useFetch(laptopLendingConfig.getSettings);
  const { get: fetchAccessoryTypes } = useFetch(laptopLendingConfig.getAccessoryTypes);

  const { save: issueLaptop, busy: issuingLaptop } = useSave(laptopLendingConfig.issueLaptop);
  const { save: returnLaptop, busy: returningLaptop } = useSave({
    ...laptopLendingConfig.returnLaptop
  });

  const loadSettings = async () => {
    if (!branchId) return;
    try {
      const response = await fetchSettings({ params: { branch_id: branchId } });
      const settingsList = response?.settings || response?.[0]?.data?.settings || [];

      const findSetting = (key) => settingsList.find(s => s.settingKey === key);
      setSettings({
        requireSignature: findSetting('require_student_signature')?.enabled || findSetting('require_student_acknowledgment')?.enabled || false,
        requirePhotoOnIssue: findSetting('require_photo_on_issue')?.enabled || findSetting('require_photos_on_issue')?.enabled || false,
        requirePhotoOnReturn: findSetting('require_photo_on_return')?.enabled || findSetting('require_photos_on_return')?.enabled || false,
        requireConditionOnIssue: findSetting('require_condition_on_issue')?.enabled || findSetting('require_condition_check')?.enabled || false,
        requireNotesWhenDamaged: findSetting('require_damage_notes')?.enabled || false,
        requireNotesWhenIssuing: findSetting('require_issue_notes')?.enabled || false,
        setExpectedReturnDate: findSetting('set_expected_return_date')?.enabled || false,
        trackAccessories: findSetting('track_accessories')?.enabled || false
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadAccessoryTypes = async () => {
    try {
      const response = await fetchAccessoryTypes();
      const types = response?.types || response?.[0]?.data?.types || [];
      setAccessoryTypes(types.map(t => ({ id: t.id, label: t.name, ...t })));
    } catch (error) {
      console.error('Failed to load accessory types:', error);
    }
  };

  useEffect(() => {
    if (visible && studentId) {
      loadLoanHistory();
      loadSettings();
      loadAccessoryTypes();
    }
  }, [visible, studentId, branchId]);

  const loadLoanHistory = async () => {
    setLoading(true);
    try {
      const response = await fetchLoanHistory({ replace: { studentId } });
      const loanList = response?.loans || response?.[0]?.data?.loans || [];
      setLoanHistory(loanList.map((loan, index) => ({ ...loan, key: loan.id || index })));
    } catch (error) {
      notification.error({ message: 'Failed to load loan history', duration: 5 });
    }
    setLoading(false);
  };

  const loadAvailableLaptops = async () => {
    if (!branchId) return;
    try {
      const response = await fetchAvailableLaptops({ replace: { branchId } });
      const laptopList = response?.laptops || response?.[0]?.data?.laptops || [];
      setAvailableLaptops(laptopList.map(laptop => ({
        id: laptop.id,
        label: `${laptop.brand || ''} ${laptop.model || ''} - ${laptop.serial_number || ''}`.trim()
      })));
    } catch (error) {
      notification.error({ message: 'Failed to load available laptops', duration: 5 });
    }
  };

  const openLendModal = () => {
    loadAvailableLaptops();
    setLendLaptopForm({
      laptopId: null,
      issueNotes: '',
      studentSignature: '',
      photo: '',
      conditionOnIssue: 'good',
      expectedReturnDate: null,
      accessories: []
    });
    if (signatureRef) {
      signatureRef.clear();
    }
    setShowLendLaptopModal(true);
  };

  const validateLendForm = () => {
    if (!lendLaptopForm.laptopId) {
      notification.warning({ message: 'Please select a laptop', duration: 3 });
      return false;
    }

    if (settings.requireNotesWhenIssuing && !lendLaptopForm.issueNotes.trim()) {
      notification.warning({ message: 'Notes are required when issuing a laptop', duration: 3 });
      return false;
    }

    if (settings.requireSignature && !lendLaptopForm.studentSignature) {
      notification.warning({ message: 'Student signature is required', duration: 3 });
      return false;
    }

    if (settings.requirePhotoOnIssue && !lendLaptopForm.photo) {
      notification.warning({ message: 'Photo is required when issuing a laptop', duration: 3 });
      return false;
    }

    if (settings.setExpectedReturnDate && !lendLaptopForm.expectedReturnDate) {
      notification.warning({ message: 'Expected return date is required', duration: 3 });
      return false;
    }

    return true;
  };

  const handleLendLaptop = async () => {
    if (!validateLendForm()) return;

    const payload = {
      laptop_id: lendLaptopForm.laptopId,
      student_id: studentId,
      issue_notes: lendLaptopForm.issueNotes,
      condition_on_issue: lendLaptopForm.conditionOnIssue,
      student_signature: lendLaptopForm.studentSignature || undefined,
      photo_data: lendLaptopForm.photo || undefined,
      expected_return_date: lendLaptopForm.expectedReturnDate
        ? lendLaptopForm.expectedReturnDate.format('YYYY-MM-DD')
        : undefined,
      accessories: lendLaptopForm.accessories.length > 0 ? lendLaptopForm.accessories : undefined
    };

    const response = await issueLaptop(payload);
    if (response?.success === 1) {
      notification.success({ message: 'Laptop issued successfully', duration: 5 });
      setShowLendLaptopModal(false);
      if (signatureRef) {
        signatureRef.clear();
      }
      loadLoanHistory();
      onLoanUpdated();
    } else {
      notification.error({
        message: response?.data?.message || 'Failed to issue laptop',
        duration: 5
      });
    }
  };

  const openReturnModal = (loan) => {
    selectedLoanRef.current = loan;
    setSelectedLoan(loan);
    const loanAccessories = loan.accessories || [];
    setReturnLaptopForm({
      condition: 'satisfactory',
      returnNotes: '',
      photo: '',
      accessoriesReturned: loanAccessories.map(a => a.accessory_type_id || a.id)
    });
    setShowReturnLaptopModal(true);
  };

  const validateReturnForm = () => {
    if (settings.requireNotesWhenDamaged && returnLaptopForm.condition === 'damaged' && !returnLaptopForm.returnNotes.trim()) {
      notification.warning({ message: 'Notes are required when laptop is damaged', duration: 3 });
      return false;
    }

    if (settings.requirePhotoOnReturn && !returnLaptopForm.photo) {
      notification.warning({ message: 'Photo is required when returning a laptop', duration: 3 });
      return false;
    }

    return true;
  };

  const handleReturnLaptop = async () => {
    const currentLoan = selectedLoanRef.current || selectedLoan;

    if (!currentLoan || !currentLoan.id) {
      notification.error({ message: 'Invalid loan selected', duration: 5 });
      return;
    }

    if (!validateReturnForm()) return;

    const payload = {
      loanId: currentLoan.id,
      condition_on_return: returnLaptopForm.condition,
      return_notes: returnLaptopForm.returnNotes,
      photo_data: returnLaptopForm.photo || undefined,
      accessories_returned: settings.trackAccessories ? returnLaptopForm.accessoriesReturned : undefined
    };

    const response = await returnLaptop(payload);
    if (response?.success === 1) {
      notification.success({ message: 'Laptop returned successfully', duration: 5 });
      setShowReturnLaptopModal(false);
      setSelectedLoan(null);
      loadLoanHistory();
      onLoanUpdated();
    } else {
      notification.error({
        message: response?.data?.message || 'Failed to return laptop',
        duration: 5
      });
    }
  };

  const getStatusDisplay = (loan) => {
    const returnedAt = loan.returnedAt || loan.returned_at;
    const condition = loan.conditionOnReturn || loan.condition_on_return;
    if (!returnedAt) return 'Checked Out';
    if (condition === 'damaged') return 'Returned (Damaged)';
    return 'Returned';
  };

  const getStatusColor = (loan) => {
    const returnedAt = loan.returnedAt || loan.returned_at;
    const condition = loan.conditionOnReturn || loan.condition_on_return;
    if (!returnedAt) return '#1890ff';
    if (condition === 'damaged') return '#ff4d4f';
    return '#52c41a';
  };

  const handleAccessoryChange = (accessoryId, checked) => {
    setLendLaptopForm(prev => ({
      ...prev,
      accessories: checked
        ? [...prev.accessories, accessoryId]
        : prev.accessories.filter(id => id !== accessoryId)
    }));
  };

  const handleReturnAccessoryChange = (accessoryId, checked) => {
    setReturnLaptopForm(prev => ({
      ...prev,
      accessoriesReturned: checked
        ? [...prev.accessoriesReturned, accessoryId]
        : prev.accessoriesReturned.filter(id => id !== accessoryId)
    }));
  };

  const laptopColumns = [
    {
      title: 'Laptop',
      key: 'laptop',
      render: (_, record) => (
        <TableText>
          {record.brand} {record.model}
        </TableText>
      )
    },
    {
      title: 'Serial Number',
      key: 'serialNumber',
      render: (_, record) => <TableText>{record.serialNumber || record.serial_number || '-'}</TableText>
    },
    {
      title: 'Issued Date',
      key: 'issuedAt',
      render: (_, record) => {
        const val = record.issuedAt || record.issued_at;
        return <TableText>{val ? moment(val).format('MM/DD/YYYY HH:mm') : '-'}</TableText>;
      }
    },
    {
      title: 'Returned Date',
      key: 'returnedAt',
      render: (_, record) => {
        const val = record.returnedAt || record.returned_at;
        return <TableText>{val ? moment(val).format('MM/DD/YYYY HH:mm') : '-'}</TableText>;
      }
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <TableText style={{ color: getStatusColor(record), fontWeight: 500 }}>
          {getStatusDisplay(record)}
        </TableText>
      )
    },
    {
      title: '',
      key: 'action',
      width: 120,
      render: (_, record) => {
        const returnedAt = record.returnedAt || record.returned_at;
        return !returnedAt && (
          <UpdateButton
            type="primary"
            onClick={e => {
              e.stopPropagation();
              openReturnModal(record);
            }}
          >
            Return
          </UpdateButton>
        );
      }
    }
  ];

  return (
    <>
      <Modal
        className="laptopDetailsModal"
        title="Laptop Lending History"
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={1000}
      >
        <ModalContainer>
          <Spin spinning={loading}>
            <Table
              dataSource={loanHistory}
              columns={laptopColumns}
              pagination={false}
            />
          </Spin>

          <ButtonWrapper>
            <AntButton type="primary" onClick={openLendModal}>
              Lend Laptop
            </AntButton>
            <AntButton onClick={onClose}>
              Close
            </AntButton>
          </ButtonWrapper>
        </ModalContainer>
      </Modal>

      <Modal
        className="lendNewLaptopModal"
        title="Lend Laptop to Student"
        visible={showLendLaptopModal}
        onCancel={() => setShowLendLaptopModal(false)}
        footer={null}
        width={820}
      >
        <ModalContainer style={{ padding: '20px 30px 40px 30px', minHeight: 'unset' }}>
          <FormBox>
            <FormGrid>
              <Label>Select Laptop *</Label>
              <Select
                placeholder="Select available laptop"
                options={availableLaptops}
                value={lendLaptopForm.laptopId}
                onChange={value =>
                  setLendLaptopForm(prev => ({ ...prev, laptopId: value }))
                }
                style={{ width: '100%' }}
              />

              {settings.requireConditionOnIssue && (
                <>
                  <Label>Condition on Issue *</Label>
                  <Select
                    placeholder="Select condition"
                    options={CONDITION_OPTIONS}
                    value={lendLaptopForm.conditionOnIssue}
                    onChange={value =>
                      setLendLaptopForm(prev => ({ ...prev, conditionOnIssue: value }))
                    }
                    style={{ width: '100%' }}
                  />
                </>
              )}

              {settings.setExpectedReturnDate && (
                <>
                  <Label>Expected Return Date *</Label>
                  <DatePicker
                    style={{ width: '100%' }}
                    value={lendLaptopForm.expectedReturnDate}
                    onChange={date =>
                      setLendLaptopForm(prev => ({ ...prev, expectedReturnDate: date }))
                    }
                    format="MM/DD/YYYY"
                    disabledDate={current => current && current < moment().startOf('day')}
                  />
                </>
              )}

              <Label>{settings.requireNotesWhenIssuing ? 'Notes *' : 'Notes'}</Label>
              <Input
                name="issueNotes"
                placeholder={settings.requireNotesWhenIssuing ? 'Required notes' : 'Optional notes'}
                value={lendLaptopForm.issueNotes}
                onChange={value =>
                  setLendLaptopForm(prev => ({ ...prev, issueNotes: value }))
                }
              />
            </FormGrid>
          </FormBox>

          {settings.trackAccessories && accessoryTypes.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <Label>Accessories Included</Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 15, marginTop: 10 }}>
                {accessoryTypes.map(accessory => (
                  <Checkbox
                    key={accessory.id}
                    checked={lendLaptopForm.accessories.includes(accessory.id)}
                    onChange={e => handleAccessoryChange(accessory.id, e.target.checked)}
                  >
                    {accessory.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          )}

          {settings.requireSignature && (
            <div style={{ marginTop: 20 }}>
              <SignaturePad
                label="Student Signature *"
                onChange={(signature, ref) => {
                  setLendLaptopForm(prev => ({ ...prev, studentSignature: signature }));
                  setSignatureRef(ref);
                }}
              />
            </div>
          )}

          {settings.requirePhotoOnIssue && (
            <div style={{ marginTop: 20 }}>
              <Label>Laptop Photo *</Label>
              <Filepicker
                onFilePicked={photo => setLendLaptopForm(prev => ({ ...prev, photo }))}
                useCamera
                buttonLabel="Take Photo"
              />
              {lendLaptopForm.photo && (
                <div style={{ marginTop: 10, color: '#52c41a' }}>Photo captured</div>
              )}
            </div>
          )}

          <ButtonWrapper style={{ marginTop: 40 }}>
            <AntButton
              type="primary"
              onClick={handleLendLaptop}
              loading={issuingLaptop}
              disabled={!lendLaptopForm.laptopId}
            >
              Lend Laptop
            </AntButton>
            <AntButton onClick={() => setShowLendLaptopModal(false)}>
              Cancel
            </AntButton>
          </ButtonWrapper>
        </ModalContainer>
      </Modal>

      <Modal
        className="returnLaptopModal"
        title="Return Laptop"
        visible={showReturnLaptopModal}
        onCancel={() => setShowReturnLaptopModal(false)}
        footer={null}
        width={900}
      >
        {(() => {
          const displayLoan = selectedLoanRef.current || selectedLoan;
          return (
        <ModalContainer style={{ padding: '20px 30px 30px 30px', minHeight: 'unset' }}>
          <FormBox style={{ maxWidth: 800, margin: '0 auto 35px auto', padding: '35px 45px' }}>
            <FormGridWide>
              <Label>Laptop</Label>
              <Input
                name="returnLaptopModel"
                value={`${displayLoan?.brand || ''} ${displayLoan?.model || ''}`}
                disabled
                onChange={() => {}}
              />

              <Label>Serial Number</Label>
              <Input
                name="returnSerialNumber"
                value={displayLoan?.serialNumber || displayLoan?.serial_number || ''}
                disabled
                onChange={() => {}}
              />
            </FormGridWide>
          </FormBox>

          <ConditionRow>
            <Label>Condition:</Label>
            <Radio.Group
              value={returnLaptopForm.condition}
              onChange={e =>
                setReturnLaptopForm(prev => ({ ...prev, condition: e.target.value }))
              }
            >
              <Radio value="satisfactory" style={{ fontSize: 16, marginRight: 30 }}>
                Satisfactory
              </Radio>
              <Radio value="damaged" style={{ fontSize: 16, marginRight: 30 }}>
                Damaged
              </Radio>
              <Radio value="not_returned" style={{ fontSize: 16 }}>
                Not Returned
              </Radio>
            </Radio.Group>
          </ConditionRow>

          {settings.trackAccessories && displayLoan?.accessories?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <Label>Accessories Returned</Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 15, marginTop: 10 }}>
                {displayLoan.accessories.map(accessory => (
                  <Checkbox
                    key={accessory.accessory_type_id || accessory.id}
                    checked={returnLaptopForm.accessoriesReturned.includes(accessory.accessory_type_id || accessory.id)}
                    onChange={e => handleReturnAccessoryChange(accessory.accessory_type_id || accessory.id, e.target.checked)}
                  >
                    {accessory.name || accessory.accessory_name || `Accessory ${accessory.id}`}
                  </Checkbox>
                ))}
              </div>
            </div>
          )}

          <CommentsLabel>
            {settings.requireNotesWhenDamaged && returnLaptopForm.condition === 'damaged'
              ? 'Return Notes (Required for damaged items):'
              : 'Return Notes:'}
          </CommentsLabel>
          <TextArea
            placeholder="Add any notes about the laptop condition..."
            value={returnLaptopForm.returnNotes}
            onChange={e =>
              setReturnLaptopForm(prev => ({ ...prev, returnNotes: e.target.value }))
            }
          />

          {settings.requirePhotoOnReturn && (
            <div style={{ marginTop: 20 }}>
              <Label>Return Photo *</Label>
              <Filepicker
                onFilePicked={photo => setReturnLaptopForm(prev => ({ ...prev, photo }))}
                useCamera
                buttonLabel="Take Photo"
              />
              {returnLaptopForm.photo && (
                <div style={{ marginTop: 10, color: '#52c41a' }}>Photo captured</div>
              )}
            </div>
          )}

          <ButtonWrapper>
            <AntButton
              type="primary"
              onClick={handleReturnLaptop}
              loading={returningLaptop}
            >
              Confirm Return
            </AntButton>
            <AntButton onClick={() => setShowReturnLaptopModal(false)}>
              Cancel
            </AntButton>
          </ButtonWrapper>
        </ModalContainer>
          );
        })()}
      </Modal>
    </>
  );
}
